import { AvCheckbox, FeatureFlags, OpenSelect, RadioButton, Select } from '@fabric-ui/components';
import { INTERNAL_ROLES, ROLES } from '../../../../types';
import { regionOptions } from '../../../Authentications/authenticationSourceConfigs';
import { TARGET_MATCHING_TYPE_ENUM } from '../../types';
import type { DestinationConfig, DestinationConfigMap, DestinationType, FieldConfig } from '../types';

const SERVICE_NOW_TABLE_OPTIONS = [
  { title: 'Incidents', value: 'incident' },
  { title: 'Requests', value: 'sc_req_item' },
  { title: 'Remediation Task', value: 'x_aai_bprm_bprm_task' },
  { title: 'Tasks', value: 'sc_task' },
] as const;

const SNOW_CMDB_TABLE_OPTIONS = [
  { title: 'CMDB CI Server', value: 'cmdb_ci_server' },
  { title: 'CMDB CI Computer', value: 'cmdb_ci_computer' },
  { title: 'CMDB CI VM', value: 'cmdb_ci_vm_instance' },
] as const;

const SNOW_IRM_TABLE_OPTIONS = [{ title: 'Policy Exceptions', value: 'sn_compliance_policy_exception' }] as const;

const AUTHENTICATION_TYPE_OPTIONS = [
  { title: 'OAuth2', value: 'OAUTH2' },
  { title: 'Basic Auth', value: 'BASIC' },
] as const;

const AUTHENTICATION_TYPE_OPTIONS_FOR_JIRA = [
  { title: 'Basic Auth', value: 'BASIC' },
  { title: 'Bearer Token', value: 'BEARER' },
] as const;

const GRANT_TYPE_OPTIONS = [
  { title: 'Password', value: 'PASSWORD' },
  { title: 'Refresh Token', value: 'REFRESH_TOKEN' },
  { title: 'Client Credentials', value: 'CLIENT_CREDENTIALS' },
] as const;

const BMC_HELIX_ISSUE_TYPE_OPTIONS = [
  { title: 'Incidents', value: 'Incident' },
  { title: 'Work Orders', value: 'WorkOrder' },
] as const;

const METHOD_OPTIONS = [
  { title: 'Post', value: 'POST' },
  { title: 'Put', value: 'PUT' },
  { title: 'Patch', value: 'PATCH' },
] as const;

const ANY_TARGET_ISSUE_LINK_SUFFIX_OPTIONS = [
  { title: 'Issue Key', value: 'ISSUE_KEY' },
  { title: 'Display Key', value: 'DISPLAY_KEY' },
] as const;

export const AUTH_TYPES = {
  OAUTH2: 'OAUTH2',
  OAUTH2_REFRESH_TOKEN: 'OAUTH2 Refresh Token',
  JWT: 'JWT',
} as const;

const hiddenField = (base: Omit<FieldConfig, 'shouldShow'>): FieldConfig => ({
  ...base,
  shouldShow: () => false,
  defaultValue: base.defaultValue ?? 'auth-config',
});

const identityDisabled = (isDisabled: boolean) => isDisabled;

const createSnowConfig = (additionalFields: FieldConfig[] = []): Pick<DestinationConfig, 'fields' | 'authSection'> => ({
  fields: [
    { label: 'Instance Name', valueName: 'domain', required: true },
    ...additionalFields,
    hiddenField({
      label: 'Authentication',
      valueName: 'authType',
      type: RadioButton,
      params: { options: [...AUTHENTICATION_TYPE_OPTIONS] },
      defaultValue: 'OAUTH2',
    }),
    hiddenField({
      label: 'Grant Type',
      valueName: 'grantType',
      required: true,
      type: Select,
      defaultValue: 'PASSWORD',
      params: {
        options: [
          { title: 'Password', value: 'PASSWORD' },
          { title: 'Refresh Token', value: 'REFRESH_TOKEN' },
        ],
      },
    }),
    hiddenField({ label: 'Username', valueName: 'username', required: true }),
    hiddenField({ label: 'Password', valueName: 'password', required: true }),
    hiddenField({ label: 'Client Id', valueName: 'clientId', required: true }),
    hiddenField({ label: 'Client Secret', valueName: 'clientSecret', required: true }),
  ],
  authSection: {
    shouldShow: () => false,
    fields: [
      { label: 'Client Id', valueName: 'oauthClientId', required: true },
      { label: 'Client Secret', valueName: 'oauthClientSecret', required: true },
      { label: 'Instance Name', valueName: 'oauthTenantId', required: true },
      { label: 'Refresh Token', valueName: 'oauthRefreshToken', required: true },
    ],
    authType: AUTH_TYPES.OAUTH2_REFRESH_TOKEN,
  },
});

const JIRA_FIELDS: FieldConfig[] = [
  hiddenField({
    label: 'Jira Organization Domain',
    valueName: 'domain',
    required: true,
    params: { placeholder: 'my-domain.atlassian.net' },
  }),
  { label: 'Project Key', valueName: 'projectKey', required: true },
  { label: 'Issue Type', valueName: 'issueType' },
  hiddenField({
    label: 'Authentication',
    valueName: 'authType',
    required: true,
    type: RadioButton,
    params: { options: [...AUTHENTICATION_TYPE_OPTIONS_FOR_JIRA] },
    defaultValue: 'BASIC',
  }),
  hiddenField({ label: 'Email', valueName: 'email', required: true }),
  hiddenField({ label: 'Jira API Key', valueName: 'apiKey', required: true }),
  {
    label: 'Proxy DNS',
    valueName: 'proxyDNS',
    required: config => !!config.proxyEnabled,
    shouldShow: config => !!config.proxyEnabled,
  },
];

const TENABLE_SETTINGS_FIELDS: FieldConfig[] = [
  {
    label: 'Scan Name',
    valueName: 'scanName',
    required: false,
    defaultValue: 'UVM Scan - Basic Network Scan',
  },
  {
    label: 'Max Number of Parallel Scans',
    valueName: 'parallelScans',
    required: false,
    type: Select,
    params: {
      options: [
        { title: '1', value: 1 },
        { title: '2', value: 2 },
        { title: '3', value: 3 },
        { title: '4', value: 4 },
        { title: '5', value: 5 },
      ],
    },
    defaultValue: 1,
  },
];

const DEFAULT_DESTINATION: DestinationConfig = {
  fields: [],
  settingsFields: [],
};

export const destinationConfigMap: DestinationConfigMap = {
  JIRA: {
    ...DEFAULT_DESTINATION,
    fields: JIRA_FIELDS,
    fieldsAfterAuth: [{ label: 'Browser Domain', valueName: 'browserDomain' }],
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: Select,
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
    ],
  },
  JIRA_DATA_CENTER: {
    ...DEFAULT_DESTINATION,
    fields: [...JIRA_FIELDS, { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: AvCheckbox, defaultValue: false }],
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: Select,
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    onPremSection: { shouldShow: true },
  },
  SLACK: {
    ...DEFAULT_DESTINATION,
    displayName: 'Slack',
    fields: [
      hiddenField({ label: 'App Token', valueName: 'appToken', required: true }),
      {
        label: 'Channel Type',
        valueName: 'channelType',
        type: RadioButton,
        required: true,
        defaultValue: 'PRIVATE_CHANNEL',
        params: {
          options: [
            { title: 'Private Channel', value: 'PRIVATE_CHANNEL' },
            { title: 'Public Channel', value: 'PUBLIC_CHANNEL' },
          ],
        },
      },
      { label: 'Channel', valueName: 'channel', required: true },
    ],
  },
  FRESHSERVICE: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Api Key', valueName: 'apiKey', required: true }),
      { label: 'Domain', valueName: 'domain', required: true },
    ],
  },
  TINES: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Domain', valueName: 'domain', required: true }),
      hiddenField({ label: 'User Token', valueName: 'userToken' }),
      hiddenField({ label: 'User Email', valueName: 'userEmail' }),
      hiddenField({ label: 'Secret', valueName: 'secret', required: true }),
    ],
    settingsFields: [
      { label: 'Path', valueName: 'path', required: true },
      { label: 'Secret', valueName: 'secret', required: true },
    ],
  },
  TORQ: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Domain', valueName: 'webhookUrl', required: true }),
      hiddenField({ label: 'Auth Header Name', valueName: 'authHeaderName' }),
      hiddenField({ label: 'Auth Header Value', valueName: 'authHeaderValue' }),
    ],
  },
  SERVICENOW: {
    ...DEFAULT_DESTINATION,
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: OpenSelect,
        defaultValue: 'incident',
        params: { options: [...SERVICE_NOW_TABLE_OPTIONS], isMultiple: false },
      },
    ],
    ...createSnowConfig(),
  },
  VEOCI: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Client Id', valueName: 'clientId', required: true }),
      hiddenField({ label: 'Client Secret', valueName: 'clientSecret', required: true }),
      { label: 'Form Id', valueName: 'formId', required: true },
    ],
    settingsFields: [{ label: 'Avalor Id Field Name', valueName: 'avalorIdFieldName', required: true }],
  },
  RAPID7: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Api Key', valueName: 'apiKey', required: true }),
      hiddenField({ label: 'Domain', valueName: 'baseDomain', required: true }),
    ],
    settingsFields: [
      { label: 'Workflow Id', valueName: 'workflowId', required: true },
      { label: 'Execution Message', valueName: 'executionMessage' },
    ],
  },
  MONDAY: {
    ...DEFAULT_DESTINATION,
    fields: [hiddenField({ label: 'Token', valueName: 'token', required: true })],
    settingsFields: [{ label: 'Board Name', valueName: 'boardName', required: true }],
  },
  SCCM: {
    ...DEFAULT_DESTINATION,
    fields: [
      { label: 'Host', valueName: 'host', required: true },
      { label: 'Port', valueName: 'port', required: true, defaultValue: 5985 },
      { label: 'Domain', valueName: 'domain' },
      { label: 'User', valueName: 'user', required: true },
      { label: 'Password', valueName: 'password', required: true },
    ],
    settingsFields: [
      { label: 'Site Code', valueName: 'sccmSiteCode', required: true },
      { label: 'Execution Message', valueName: 'executionMessage', defaultValue: 'Triggered patch via SCCM' },
      {
        label: 'Deployment Creation Arguments',
        valueName: 'deploymentCreationArguments',
        defaultValue:
          '-SoftwareInstallation $true -DeployWithNoPackage $true -SendWakeupPacket $true -DownloadFromMicrosoftUpdate $true -AvailableDateTime (Get-Date) -DeadlineDateTime (Get-Date).AddDays(7)',
      },
      { label: 'Assets Postfix', valueName: 'assetsPostfix' },
      {
        label: 'Debug Mode For Builtin Script',
        valueName: 'debugMode',
        type: AvCheckbox,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
      {
        label: 'Use Debug Script',
        valueName: 'useDebugScript',
        type: AvCheckbox,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
      {
        label: 'Debug Script',
        valueName: 'debugScript',
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
    ],
  },
  BMCHELIX: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Domain', valueName: 'domain', required: true }),
      hiddenField({ label: 'Port', valueName: 'port', required: true }),
      hiddenField({ label: 'User', valueName: 'username', required: true }),
      hiddenField({ label: 'Password', valueName: 'password', required: true }),
      { label: 'Use HTTPS', valueName: 'useHttps', type: AvCheckbox },
    ],
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: Select,
        params: { options: [...BMC_HELIX_ISSUE_TYPE_OPTIONS] },
      },
    ],
  },
  EMAIL: {
    ...DEFAULT_DESTINATION,
    displayName: 'Email',
    validateFieldsOnConnect: true,
    fields: [
      {
        label: 'Recipients',
        valueName: 'recipients',
        required: true,
        type: OpenSelect,
        defaultValue: [],
        params: { isMultiple: true },
      },
      {
        label: 'Subject',
        valueName: 'subject',
        required: false,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
        defaultValue: ' ',
        shouldShow: () => false,
      },
      {
        label: 'Body',
        valueName: 'body',
        required: false,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
        defaultValue: ' ',
        shouldShow: () => false,
      },
    ],
  },
  TEAMS: {
    ...DEFAULT_DESTINATION,
    displayName: 'Teams',
    fields: [
      { label: 'Group ID', valueName: 'groupId', required: true },
      { label: 'Channel ID', valueName: 'channelId', required: true },
    ],
    authSection: {
      shouldShow: () => false,
      fields: [
        { label: 'Client Id', valueName: 'oauthClientId', required: true },
        { label: 'Client Secret', valueName: 'oauthClientSecret', required: true },
        { label: 'Tenant Id', valueName: 'oauthTenantId', required: true },
      ],
      authType: AUTH_TYPES.OAUTH2,
    },
  },
  S3: {
    ...DEFAULT_DESTINATION,
    displayName: 'S3',
    fields: [
      { label: 'Account', valueName: 'awsAccount', required: true },
      {
        label: 'Region',
        valueName: 'region',
        required: true,
        type: Select,
        params: { options: regionOptions },
      },
      { label: 'Bucket', valueName: 'bucket', required: true },
      { label: 'Role ARN', valueName: 'roleArn', required: true },
      { label: 'External ID', valueName: 'externalId' },
    ],
    settingsFields: [{ label: 'Path', valueName: 'path' }],
  },
  GCP_STORAGE: {
    ...DEFAULT_DESTINATION,
    displayName: 'GCP Storage',
    requiredFeatureFlag: FeatureFlags.GcpStorageOutegration,
    skipSettingsStep: true,
    fields: [
      { label: 'Bucket', valueName: 'bucket', required: true },
      { label: 'Project ID', valueName: 'projectId' },
      { label: 'Path', valueName: 'path' },
    ],
  },
  ANYTARGET: {
    ...DEFAULT_DESTINATION,
    fields: [
      {
        label: 'Test Connection Path',
        valueName: 'testConnectionPath',
        required: false,
        params: { placeholder: '/rest/api/test' },
      },
      {
        label: 'Authentication Type',
        valueName: 'authType',
        required: true,
        type: RadioButton,
        params: { options: [...AUTHENTICATION_TYPE_OPTIONS] },
        defaultValue: 'OAUTH2',
      },
      {
        label: 'Username',
        valueName: 'username',
        required: config => config.authType === 'BASIC',
        shouldShow: config => config.authType === 'BASIC',
      },
      {
        label: 'Password',
        valueName: 'password',
        required: config => config.authType === 'BASIC',
        shouldShow: config => config.authType === 'BASIC',
      },
      {
        label: 'Grant Type',
        valueName: 'grantType',
        required: true,
        shouldShow: config => config.authType === 'OAUTH2',
        type: Select,
        defaultValue: 'CLIENT_CREDENTIALS',
        params: { options: [...GRANT_TYPE_OPTIONS] },
      },
      {
        label: 'Client Id',
        valueName: 'clientId',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: config => config.authType === 'OAUTH2' && config.grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Client Secret',
        valueName: 'clientSecret',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: config => config.authType === 'OAUTH2' && config.grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Token URL',
        valueName: 'tokenUrl',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: config => config.authType === 'OAUTH2' && config.grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Auth Headers',
        valueName: 'authHeaders',
        required: false,
        params: { placeholder: '{"FieldName1": "FieldValue1", "FieldName2": "FieldValue2"}' },
      },
      { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: AvCheckbox, defaultValue: false },
    ],
    authSection: {
      shouldShow: () => false,
      fields: [],
      authType: AUTH_TYPES.OAUTH2_REFRESH_TOKEN,
    },
    onPremSection: { shouldShow: true },
    settingsFields: [
      { label: 'Base URL', valueName: 'baseUrl', required: true, params: { placeholder: 'https://example.com' } },
      {
        label: 'Create Issue Path',
        valueName: 'createIssuePath',
        required: true,
        params: { placeholder: '/rest/api/issue' },
      },
      { label: 'Payload Root Field Name', valueName: 'payloadRootFieldName', required: false },
      {
        label: 'Additional Create Issue Payload Fields',
        valueName: 'additionalCreateIssuePayloadField',
        required: false,
        params: { placeholder: '{"FieldName1": "FieldValue1", "FieldName2": "FieldValue2"}' },
      },
      { label: 'Issue Key Path', valueName: 'issueKeyPath', params: { placeholder: 'issue.key' } },
      {
        label: 'Async Response',
        valueName: 'asyncResponse',
        required: true,
        type: AvCheckbox,
        defaultValue: false,
      },
      {
        label: 'Sub Issue Read Path',
        valueName: 'subIssueReadPath',
        required: false,
        params: { placeholder: '/rest/api/sub-issue/{subIssueReadPath}' },
      },
      { label: 'Sub Issue Key Path', valueName: 'subIssueKeyPath', required: false },
      {
        label: 'Identifier Ticket Field Name',
        valueName: 'identifierTicketFieldName',
        required: false,
        shouldShow: config => !!config.asyncResponse,
      },
      { label: 'Issue Display Key Path', valueName: 'issueDisplayKeyPath', required: false },
      {
        label: 'Issue Link URL',
        valueName: 'issueLinkUrl',
        required: false,
        params: { placeholder: 'https://example.com/issue/' },
      },
      {
        label: 'Issue Link Suffix',
        valueName: 'issueLinkSuffix',
        required: false,
        params: { options: [...ANY_TARGET_ISSUE_LINK_SUFFIX_OPTIONS], placeholder: 'Issue Key' },
        type: Select,
        defaultValue: 'ISSUE_KEY',
      },
      {
        label: 'Update Issue Path',
        valueName: 'updateIssuePath',
        required: false,
        params: { placeholder: '/rest/api/issue/{externalTicketKey}' },
      },
      {
        label: 'Update Issue Method',
        valueName: 'updateIssueMethod',
        required: config => !!config.updateIssuePath,
        params: { options: [...METHOD_OPTIONS] },
        type: RadioButton,
        defaultValue: 'PUT',
      },
      { label: 'Webhook Key Field Name', valueName: 'webhookKeyFieldName', required: false },
      { label: 'Webhook Change Fields Field Name', valueName: 'webhookChangedFieldsFieldName', required: false },
      { label: 'Webhook Deleted Field Name', valueName: 'webhookIsDeletedFieldName', required: false },
      {
        label: 'Comments URL',
        valueName: 'commentsUrl',
        required: false,
        params: { placeholder: 'https://example.com/ticket/{externalTicketKey}/comments' },
      },
      {
        label: 'Comments Method',
        valueName: 'commentsMethod',
        required: config => !!config.updateIssuePath,
        params: { options: [...METHOD_OPTIONS] },
        type: RadioButton,
        defaultValue: 'PUT',
      },
      {
        label: 'Comments Custom Headers',
        valueName: 'commentsCustomHeaders',
        required: false,
        params: { placeholder: '{"FieldName1": "FieldValue1", "FieldName2": "FieldValue2"}' },
      },
    ],
  },
  KAFKA: {
    ...DEFAULT_DESTINATION,
    fields: [
      { label: 'Bootstrap Servers', valueName: 'bootstrapServers', required: true },
      { label: 'Topic', valueName: 'topic', required: true },
    ],
  },
  SNOW_CMDB: {
    ...DEFAULT_DESTINATION,
    defaultFields: {
      projectionName: 'violation_tickets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
      matchingCondition: { stringCondition: { in: { values: [] } }, fieldName: 'violation_ticket.title' },
    },
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: OpenSelect,
        defaultValue: 'cmdb_ci_server',
        params: { options: [...SNOW_CMDB_TABLE_OPTIONS], isMultiple: false },
      },
    ],
    ...createSnowConfig(),
  },
  SERVICE_NOW_SERVICE_CATALOG: {
    ...DEFAULT_DESTINATION,
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: OpenSelect,
        defaultValue: 'sc_task',
        params: { options: [...SERVICE_NOW_TABLE_OPTIONS], isMultiple: false },
      },
    ],
    ...createSnowConfig([{ label: 'Item ID', valueName: 'itemId', required: true }]),
  },
  DSI: {
    ...DEFAULT_DESTINATION,
    fields: [{ label: 'Data Source ID', valueName: 'dataSourceInstanceId', required: true }],
  },
  HALO: {
    ...DEFAULT_DESTINATION,
    fields: [
      hiddenField({ label: 'Domain', valueName: 'domain', required: true }),
      hiddenField({ label: 'Client Id', valueName: 'clientId', required: true }),
      hiddenField({ label: 'Client Secret', valueName: 'clientSecret', required: true }),
      hiddenField({ label: 'Tenant', valueName: 'tenant', required: true }),
      { label: 'Issue Type', valueName: 'issueType', required: true, defaultValue: 'Incident' },
    ],
  },
  SERVICE_NOW_IRM: {
    ...DEFAULT_DESTINATION,
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: OpenSelect,
        defaultValue: 'sn_compliance_policy_exception',
        params: { options: [...SNOW_IRM_TABLE_OPTIONS], isMultiple: false },
      },
    ],
    ...createSnowConfig(),
  },
  MANAGE_ENGINE: {
    ...DEFAULT_DESTINATION,
    settingsFields: [{ label: 'App Domain', valueName: 'appDomain', required: false }],
    fields: [
      { label: 'Domain Name', valueName: 'domainName', required: true },
      {
        label: 'Auth Headers',
        valueName: 'authHeaders',
        required: false,
        params: { placeholder: '{"FieldName1": "FieldValue1", "FieldName2": "FieldValue2"}' },
      },
    ],
  },
  AZURE_DEVOPS: {
    ...DEFAULT_DESTINATION,
    settingsFields: [
      {
        label: 'Work item type',
        valueName: 'workItemType',
        required: true,
        type: Select,
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    fields: [
      { label: 'Organization', valueName: 'organization', required: true },
      { label: 'Project', valueName: 'project', required: true },
    ],
  },
  QUALYS_PM: DEFAULT_DESTINATION,
  TENABLE: {
    ...DEFAULT_DESTINATION,
    defaultFields: {
      projectionName: 'uber_assets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
    },
    uiComponentsConfig: { disableProjectionSelect: true },
    settingsFields: [
      ...TENABLE_SETTINGS_FIELDS,
      {
        label: 'Scan Template',
        valueName: 'scanTemplate',
        required: true,
        type: Select,
        defaultValue: 'Basic Network Scan',
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
      {
        label: 'Folder Name',
        valueName: 'folderName',
        required: false,
        params: { placeholder: 'UVM Scans' },
      },
    ],
  },
  TENABLE_SC: {
    ...DEFAULT_DESTINATION,
    defaultFields: {
      projectionName: 'uber_assets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
    },
    uiComponentsConfig: { disableProjectionSelect: true },
    onPremSection: { shouldShow: true },
    settingsFields: [
      ...TENABLE_SETTINGS_FIELDS,
      {
        label: 'Scan Policy',
        valueName: 'scanPolicy',
        required: true,
        type: Select,
        defaultValue: 'Basic Network Scan Policy',
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
      {
        label: 'Repository',
        valueName: 'repository',
        required: true,
        type: Select,
        disabled: identityDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    fields: [
      {
        label: 'Proxy DNS',
        valueName: 'proxyDNS',
        required: config => !!config.proxyEnabled,
        shouldShow: config => !!config.proxyEnabled,
      },
      { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: AvCheckbox, defaultValue: false },
    ],
  },
  SPLUNK: {
    requiredFeatureFlag: FeatureFlags.SplunkOutegration,
    defaultFields: {
      projectionName: 'incidents',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.NEVER,
    },
    uiComponentsConfig: { disableProjectionSelect: true },
    settingsFields: [],
    fields: [
      {
        label: 'Base URL',
        valueName: 'url',
        required: true,
        params: { placeholder: 'https://example.com:port' },
      },
    ],
  },
  CROWDSTRIKE: {
    requiredFeatureFlag: FeatureFlags.CrowdStrikeOutegration,
    defaultFields: {
      projectionName: 'incidents',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.NEVER,
    },
    uiComponentsConfig: { disableProjectionSelect: true },
    settingsFields: [],
    fields: [
      {
        label: 'Base URL',
        valueName: 'url',
        required: true,
        params: { placeholder: 'https://example.com:port' },
      },
    ],
  },
};

export const getDestinationConfig = (type: string): DestinationConfig | undefined => destinationConfigMap[type as DestinationType];

export const hasDestinationConfig = (type: string): boolean => type in destinationConfigMap;

const collectDefaults = (fields: FieldConfig[] | undefined, seed: Record<string, unknown>): Record<string, unknown> => {
  if (!fields?.length) return seed;
  return fields.reduce<Record<string, unknown>>(
    (obj, { valueName, defaultValue }) => (defaultValue !== undefined ? { ...obj, [valueName]: defaultValue } : obj),
    seed
  );
};

export const getDestinationDisplayName = (type: string): string => getDestinationConfig(type)?.displayName || type;

export const getDefaultConfigFieldsValues = ({ type }: { type: string | undefined }): Record<string, unknown> => {
  if (!type) return {};
  return collectDefaults(getDestinationConfig(type)?.fields, { type });
};

export const getDefaultSettingFieldsValues = ({ type }: { type: string | undefined }): Record<string, unknown> => {
  if (!type) return {};
  return collectDefaults(getDestinationConfig(type)?.settingsFields, {});
};
